import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  FileText,
  Loader2,
  MessageSquareText,
  Plus,
  Send,
  Trash2,
  WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Pagination from '@/client/components/common/Pagination';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { MarketingPageShell } from './components/MarketingPageShell';
import { ActionIconButton } from './components/ActionIconButton';
import { StatusBadge, DeleteConfirmDialog, EmptyState } from './components/marketing-ui';
import {
  useContentQuery,
  useDeleteContentMutation,
} from '@/client/api/marketing/studio';
import { usePlaysQuery, useSeriesQuery } from '@/client/api/marketing/plays';
import type { ApiContentItem } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  CONTENT_STATUS_BADGE,
  CONTENT_STATUS_LABEL,
  CONTENT_TYPE_LABEL,
  type ContentStatus,
  type ContentType,
} from '@/client/types/marketing';

/**
 * Studio → Content, on real data.
 *
 * The three per-row counts — variants, how many are already queued, and open
 * feedback — come from the server with `with_counts=1`. Computing them here
 * would mean loading every variant, every distribution row and every feedback
 * thread into the browser to render three small numbers per row.
 *
 * Deleting is the only mutation on this page. Status moves live in the editor
 * and on Approvals, because the gated ones need an approver stamp that a list
 * view has no business issuing.
 */

const TYPE_FILTERS: (ContentType | 'all')[] = [
  'all',
  'campaign',
  'social_package',
  'article',
  'blog_post',
  'press_release',
  'newsletter',
  'video_script',
  'landing_page',
];

const STATUS_FILTERS: (ContentStatus | 'all')[] = [
  'all',
  'idea',
  'draft',
  'in_review',
  'approved',
  'scheduled',
  'published',
  'archived',
];

const STATUS_PIPELINE: ContentStatus[] = [
  'idea',
  'draft',
  'in_review',
  'approved',
  'scheduled',
  'published',
];

const STATUS_BAR: Partial<Record<ContentStatus, string>> = {
  idea: 'bg-gray-300',
  draft: 'bg-slate-400',
  in_review: 'bg-amber-500',
  approved: 'bg-sky-500',
  scheduled: 'bg-indigo-500',
  published: 'bg-emerald-500',
  archived: 'bg-gray-200',
};

type ContentSort = 'updated' | 'title' | 'status' | 'variants';

const STATUS_RANK: Record<string, number> = {
  in_review: 0,
  draft: 1,
  idea: 2,
  approved: 3,
  scheduled: 4,
  published: 5,
  archived: 6,
};

const inputClass =
  'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const asTags = (value: unknown): string[] =>
  Array.isArray(value) ? (value as unknown[]).map(String) : [];

function InsightTile({
  label,
  value,
  meta,
  bar,
  icon: Icon,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  meta?: string[];
  bar?: { value: number; className: string };
  icon: ComponentType<{ className?: string }>;
  tone: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex h-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left',
        onClick && 'transition hover:border-blue-300 hover:bg-blue-50/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <span className={cn('flex h-6 w-6 items-center justify-center rounded-md', tone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums leading-none text-gray-900">
        {value}
      </div>
      {bar && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full', bar.className)}
            style={{ width: `${Math.min(100, Math.max(0, bar.value * 100))}%` }}
          />
        </div>
      )}
      {meta && meta.length > 0 && (
        <p
          className="mt-1.5 truncate text-[11px] leading-snug text-gray-500"
          title={meta.join(' · ')}
        >
          {meta.join(' · ')}
        </p>
      )}
    </Tag>
  );
}

const ContentPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading, isError, error } = useContentQuery({ limit: 200, with_counts: 1 });
  const plays = usePlaysQuery({ limit: 100 });
  const seriesQ = useSeriesQuery({ limit: 100 });
  const removeContent = useDeleteContentMutation();

  const [typeFilter, setTypeFilter] = useState<ContentType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [playFilter, setPlayFilter] = useState(searchParams.get('play') ?? 'all');
  const [seriesFilter, setSeriesFilter] = useState(searchParams.get('series') ?? 'all');
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'open'>('all');
  const [sort, setSort] = useState<ContentSort>('updated');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleting, setDeleting] = useState<ApiContentItem | null>(null);

  const items: ApiContentItem[] = data?.items ?? [];
  const playList = plays.data?.items ?? [];
  const seriesList = seriesQ.data?.items ?? [];

  const typeFilterOptions = useMemo(
    () =>
      TYPE_FILTERS.map((t) => ({
        value: t,
        label: t === 'all' ? 'All types' : CONTENT_TYPE_LABEL[t],
      })),
    [],
  );

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTERS.map((s) => ({
        value: s,
        label: s === 'all' ? 'All statuses' : CONTENT_STATUS_LABEL[s],
      })),
    [],
  );

  const playFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All plays' },
      { value: 'none', label: 'No play' },
      ...playList.map((p) => ({
        value: String(p.play_id),
        label: p.name,
      })),
    ],
    [playList],
  );

  const seriesFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All series' },
      { value: 'any', label: 'Any series' },
      { value: 'none', label: 'No series' },
      ...seriesList.map((s) => ({
        value: String(s.series_id),
        label: s.name,
      })),
    ],
    [seriesList],
  );

  const feedbackFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All feedback' },
      { value: 'open', label: 'Open feedback' },
    ],
    [],
  );

  const sortOptions = useMemo(
    () => [
      { value: 'updated', label: 'Sort: Updated' },
      { value: 'title', label: 'Sort: Title' },
      { value: 'status', label: 'Sort: Status' },
      { value: 'variants', label: 'Sort: Variants' },
    ],
    [],
  );

  const filtersActive =
    typeFilter !== 'all' ||
    statusFilter !== 'all' ||
    playFilter !== 'all' ||
    seriesFilter !== 'all' ||
    feedbackFilter !== 'all' ||
    search.trim().length > 0;

  const playName = (id: number | null) =>
    playList.find((p) => p.play_id === id)?.name ?? null;
  const seriesLabel = (id: number | null) =>
    id == null ? undefined : seriesList.find((s) => s.series_id === id)?.name;

  useEffect(() => {
    const fromUrl = searchParams.get('series') ?? 'all';
    setSeriesFilter((prev) => (fromUrl !== prev ? fromUrl : prev));
  }, [searchParams]);

  const setPlayFilterAndUrl = (value: string) => {
    setPlayFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('play');
    else next.set('play', value);
    setSearchParams(next, { replace: true });
  };

  const setSeriesFilterAndUrl = (value: string) => {
    setSeriesFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('series');
    else next.set('series', value);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setStatusFilter('all');
    setPlayFilterAndUrl('all');
    setSeriesFilterAndUrl('all');
    setFeedbackFilter('all');
    setSearch('');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter((item) => {
      if (typeFilter !== 'all' && item.content_type !== typeFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (playFilter === 'none' && item.play_id != null) return false;
      if (
        playFilter !== 'all' &&
        playFilter !== 'none' &&
        String(item.play_id) !== playFilter
      ) {
        return false;
      }
      if (seriesFilter === 'any' && item.series_id == null) return false;
      if (seriesFilter === 'none' && item.series_id != null) return false;
      if (
        seriesFilter !== 'all' &&
        seriesFilter !== 'any' &&
        seriesFilter !== 'none' &&
        String(item.series_id) !== seriesFilter
      ) {
        return false;
      }
      if (feedbackFilter === 'open' && !(item.open_feedback_count ?? 0)) return false;
      if (
        q &&
        !(
          item.title.toLowerCase().includes(q) ||
          (item.excerpt ?? '').toLowerCase().includes(q) ||
          asTags(item.tags).some((t) => t.toLowerCase().includes(q)) ||
          (playName(item.play_id) ?? '').toLowerCase().includes(q) ||
          (seriesLabel(item.series_id) ?? '').toLowerCase().includes(q)
        )
      ) {
        return false;
      }
      return true;
    });

    list.sort((a, b) => {
      switch (sort) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'status':
          return (
            (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9) ||
            a.title.localeCompare(b.title)
          );
        case 'variants':
          return (b.variant_count ?? 0) - (a.variant_count ?? 0);
        case 'updated':
        default:
          return (b.date_modified ?? '').localeCompare(a.date_modified ?? '');
      }
    });
    return list;
  }, [
    items,
    typeFilter,
    statusFilter,
    playFilter,
    seriesFilter,
    feedbackFilter,
    search,
    playList,
    seriesList,
    sort,
  ]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, statusFilter, playFilter, seriesFilter, feedbackFilter, search, sort]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filtered.length / limit) || 1);
    if (page > max) setPage(max);
  }, [filtered.length, limit, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, page, limit]);

  const insights = useMemo(() => {
    const byStatus: Record<string, number> = {};
    for (const s of STATUS_FILTERS) if (s !== 'all') byStatus[s] = 0;
    for (const item of items) byStatus[item.status] = (byStatus[item.status] ?? 0) + 1;

    const inReview = byStatus.in_review ?? 0;
    const openFeedback = items.reduce((n, i) => n + (i.open_feedback_count ?? 0), 0);
    const published = byStatus.published ?? 0;
    const scheduled = byStatus.scheduled ?? 0;
    const totalVariants = items.reduce((n, i) => n + (i.variant_count ?? 0), 0);
    const withVariants = items.filter((i) => (i.variant_count ?? 0) > 0).length;
    // Has variants authored but none materialised into a distribution row.
    const readyToQueue = items.filter(
      (i) =>
        (i.variant_count ?? 0) > 0 &&
        (i.queued_count ?? 0) === 0 &&
        ['approved', 'draft', 'in_review'].includes(i.status),
    ).length;
    const unlinked = items.filter((i) => i.play_id == null).length;
    const shippingPct = items.length ? (published + scheduled) / items.length : 0;

    const callouts: { tone: string; text: string; action?: () => void }[] = [];
    if (inReview > 0) {
      callouts.push({
        tone: 'border-amber-200 bg-amber-50 text-amber-900',
        text: `${inReview} piece${inReview === 1 ? '' : 's'} in review — clear them on Approvals or open the editor.`,
        action: () => setStatusFilter('in_review'),
      });
    }
    if (openFeedback > 0) {
      callouts.push({
        tone: 'border-rose-200 bg-rose-50 text-rose-900',
        text: `${openFeedback} open feedback thread${openFeedback === 1 ? '' : 's'} waiting on content.`,
        action: () => setFeedbackFilter('open'),
      });
    }
    if (readyToQueue > 0) {
      callouts.push({
        tone: 'border-sky-200 bg-sky-50 text-sky-900',
        text: `${readyToQueue} piece${readyToQueue === 1 ? '' : 's'} have variants but aren’t in a distribute queue yet.`,
        action: () => {
          setStatusFilter('all');
          setSort('variants');
        },
      });
    }
    if (unlinked > 0) {
      callouts.push({
        tone: 'border-violet-200 bg-violet-50 text-violet-900',
        text: `${unlinked} piece${unlinked === 1 ? '' : 's'} aren’t linked to a play.`,
        action: () => setPlayFilterAndUrl('none'),
      });
    }
    if (callouts.length === 0 && items.length > 0) {
      callouts.push({
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        text: 'Pipeline looks healthy — keep drafting and queueing variants into Distribute.',
      });
    }

    return {
      byStatus,
      inReview,
      openFeedback,
      published,
      scheduled,
      withVariants,
      readyToQueue,
      unlinked,
      shippingPct,
      callouts,
      totalVariants,
    };
  }, [items]);

  const confirmDelete = () => {
    if (!deleting) return;
    const title = deleting.title;
    removeContent.mutate(
      { mciid: deleting.mciid },
      {
        onSuccess: () => toast.success(`Removed “${title}”`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the content')),
      },
    );
    setDeleting(null);
  };

  return (
    <MarketingPageShell
      title="Content"
      description="Draft, review, and ship master pieces — then queue platform variants into Distribute."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Studio' },
        { label: 'Content' },
      ]}
      icon={FileText}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="gap-2" asChild>
            <Link to="/admin/marketing/crew/feedback">
              <MessageSquareText className="h-4 w-4" /> Feedback
              {insights.openFeedback > 0 ? (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                  {insights.openFeedback}
                </span>
              ) : null}
            </Link>
          </Button>
          <Button
            type="button"
            className="gap-2"
            onClick={() => navigate('/admin/marketing/studio/content/new')}
          >
            <Plus className="h-4 w-4" /> New content
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading content…
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load content</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <InsightTile
                label="Pipeline"
                value={String(items.length)}
                meta={[
                  `${(insights.byStatus.draft ?? 0) + (insights.byStatus.idea ?? 0)} drafting`,
                  `${insights.inReview} review`,
                ]}
                icon={FileText}
                tone="bg-blue-50 text-blue-700"
                onClick={() => setStatusFilter('all')}
              />
              <InsightTile
                label="In review"
                value={String(insights.inReview)}
                meta={[`${insights.openFeedback} open feedback`]}
                bar={{
                  value: items.length ? insights.inReview / items.length : 0,
                  className: insights.inReview > 0 ? 'bg-amber-500' : 'bg-emerald-500',
                }}
                icon={MessageSquareText}
                tone={
                  insights.inReview > 0
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-emerald-50 text-emerald-700'
                }
                onClick={() => setStatusFilter('in_review')}
              />
              <InsightTile
                label="Ready to queue"
                value={String(insights.readyToQueue)}
                meta={[
                  `${insights.totalVariants} variants`,
                  `${insights.withVariants} with destinations`,
                ]}
                icon={Send}
                tone="bg-sky-50 text-sky-700"
                onClick={() => setSort('variants')}
              />
              <InsightTile
                label="Shipped"
                value={String(insights.published + insights.scheduled)}
                meta={[`${insights.published} live`, `${insights.scheduled} scheduled`]}
                bar={{ value: insights.shippingPct, className: 'bg-emerald-500' }}
                icon={CheckCircle2}
                tone="bg-emerald-50 text-emerald-700"
                onClick={() => setStatusFilter('published')}
              />
            </div>

            <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
              <div className="rounded-lg border border-gray-200 bg-white p-3.5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-gray-900">Insights</h2>
                  <Link
                    to="/admin/marketing/crew/approvals"
                    className="text-[11px] font-medium text-blue-700 hover:underline"
                  >
                    Approvals →
                  </Link>
                </div>
                {insights.callouts.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    No content yet — start a piece and give it a play.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {insights.callouts.slice(0, 3).map((c, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          disabled={!c.action}
                          onClick={c.action}
                          className={cn(
                            'w-full rounded-md border px-2.5 py-1.5 text-left text-xs leading-snug',
                            c.tone,
                            c.action && 'hover:opacity-90',
                            !c.action && 'cursor-default',
                          )}
                        >
                          {c.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-3.5">
                <h2 className="mb-2 text-sm font-semibold text-gray-900">Status mix</h2>
                <div className="mb-2 flex h-2 overflow-hidden rounded-full bg-gray-100">
                  {STATUS_PIPELINE.map((s) => {
                    const n = insights.byStatus[s] ?? 0;
                    if (!n || items.length === 0) return null;
                    return (
                      <div
                        key={s}
                        className={STATUS_BAR[s]}
                        style={{ width: `${(n / items.length) * 100}%` }}
                        title={`${CONTENT_STATUS_LABEL[s]}: ${n}`}
                      />
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                  {STATUS_PIPELINE.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatusFilter(s)}
                      className="flex items-center justify-between rounded px-1 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                    >
                      <span className="inline-flex items-center gap-1.5 truncate">
                        <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_BAR[s])} />
                        {CONTENT_STATUS_LABEL[s]}
                      </span>
                      <span className="font-medium tabular-nums text-gray-900">
                        {insights.byStatus[s] ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <MarketingFilterPanel
            summary={marketingMatchSummary(filtered.length, items.length, 'piece', isLoading)}
            showClear={filtersActive}
            onClear={clearFilters}
          >
            <MarketingFilterSearch
              value={search}
              onChange={setSearch}
              placeholder="Search title, tags, play, series…"
            />
            <MarketingFilterSelect
              options={typeFilterOptions}
              value={typeFilter}
              onChange={(value) => setTypeFilter(value as ContentType | 'all')}
              placeholder="All types"
              clearValue="all"
              className={marketingFilterSelectClass}
            />
            <MarketingFilterSelect
              options={statusFilterOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as ContentStatus | 'all')}
              placeholder="All statuses"
              clearValue="all"
              className={marketingFilterSelectClass}
            />
            <MarketingFilterSelect
              options={playFilterOptions}
              value={playFilter}
              onChange={setPlayFilterAndUrl}
              placeholder="All plays"
              clearValue="all"
              loading={plays.isLoading}
              className={cn(marketingFilterSelectClass, 'sm:min-w-[12rem]')}
            />
            <MarketingFilterSelect
              options={seriesFilterOptions}
              value={seriesFilter}
              onChange={setSeriesFilterAndUrl}
              placeholder="All series"
              clearValue="all"
              loading={seriesQ.isLoading}
              className={cn(marketingFilterSelectClass, 'sm:min-w-[12rem]')}
            />
            <MarketingFilterSelect
              options={feedbackFilterOptions}
              value={feedbackFilter}
              onChange={(value) => setFeedbackFilter(value as 'all' | 'open')}
              placeholder="All feedback"
              clearValue="all"
              className={marketingFilterSelectClass}
            />
            <MarketingFilterSelect
              options={sortOptions}
              value={sort}
              onChange={(value) => setSort(value as ContentSort)}
              placeholder="Sort"
              clearValue="updated"
              className={marketingFilterSelectClass}
            />
          </MarketingFilterPanel>

          {items.length === 0 ? (
            <EmptyState label="No content yet — start the first piece, or draft one from a series." />
          ) : filtered.length === 0 ? (
            <EmptyState label="No content matches these filters." />
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <TooltipProvider delayDuration={200}>
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2.5 font-medium">Title</th>
                      <th className="px-3 py-2.5 font-medium">Play</th>
                      <th className="px-3 py-2.5 font-medium">Type</th>
                      <th className="px-3 py-2.5 font-medium">Status</th>
                      <th className="px-3 py-2.5 font-medium">Variants</th>
                      <th className="px-3 py-2.5 font-medium">Updated</th>
                      <th className="px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paged.map((item) => {
                      const openCount = item.open_feedback_count ?? 0;
                      const variants = item.variant_count ?? 0;
                      const queued = item.queued_count ?? 0;
                      const seriesName = seriesLabel(item.series_id);
                      return (
                        <tr
                          key={item.mciid}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() =>
                            navigate(`/admin/marketing/studio/content/${item.mciid}`)
                          }
                        >
                          <td className="max-w-[18rem] px-3 py-2.5">
                            <p className="truncate font-medium text-gray-900">{item.title}</p>
                            <p className="truncate text-[11px] text-gray-400">
                              {seriesName ? `Series · ${seriesName}` : item.excerpt || '—'}
                            </p>
                          </td>
                          <td className="max-w-[8rem] px-3 py-2.5 text-xs text-gray-600">
                            <span className="line-clamp-2">{playName(item.play_id) ?? '—'}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                            {CONTENT_TYPE_LABEL[item.content_type as ContentType] ??
                              item.content_type}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-col gap-1">
                              <StatusBadge
                                label={
                                  CONTENT_STATUS_LABEL[item.status as ContentStatus] ?? item.status
                                }
                                className={CONTENT_STATUS_BADGE[item.status as ContentStatus] ?? ''}
                              />
                              {openCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700">
                                  <MessageSquareText className="h-3 w-3" />
                                  {openCount} open
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="max-w-[9rem] px-3 py-2.5 text-xs text-gray-600">
                            {variants === 0 ? (
                              <span className="text-gray-400">None</span>
                            ) : (
                              <div>
                                <p className="font-medium text-gray-800">{variants}</p>
                                {/* Partly-queued is its own state: some variants
                                    shipped, some did not, and "queued" would hide
                                    the ones still sitting there. */}
                                <p className="text-[11px] text-gray-400">
                                  {queued === 0 ? (
                                    <span className="text-sky-600">none queued</span>
                                  ) : queued >= variants ? (
                                    <span className="text-emerald-600">all queued</span>
                                  ) : (
                                    <span className="text-amber-600">
                                      {queued} of {variants} queued
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                            {item.date_modified
                              ? new Date(item.date_modified).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <ActionIconButton
                              label="Delete"
                              stopPropagation
                              onClick={() => setDeleting(item)}
                              className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </ActionIconButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </TooltipProvider>
              </div>
              <div className="mt-3">
                <Pagination
                  total={filtered.length}
                  page={page}
                  limit={limit}
                  itemsPerPage={limit}
                  setPage={setPage}
                  setItemsPerPage={(n) => {
                    setLimit(n);
                    setPage(1);
                  }}
                  showSelect
                  selectOptions={[10, 20, 50]}
                  compact
                />
              </div>
            </>
          )}
        </>
      )}

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete content?"
        description={
          deleting
            ? `"${deleting.title}" will be removed${
                (deleting.queued_count ?? 0) > 0
                  ? `. ${deleting.queued_count} distribution row(s) already queued from it will keep their copy but lose the link.`
                  : '.'
              }`
            : ''
        }
        onConfirm={confirmDelete}
      />
    </MarketingPageShell>
  );
};

export default ContentPage;
